import { t } from 'i18next';
import { orderBy, startCase, upperCase } from 'lodash-es';
import { browserTimeZone, dateDisplayTimeMinutesSeconds } from 'utils/timeHelpers';
import dayjs from 'utils/customDayJS';
import distance from '@turf/distance';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Box } from '@mui/material';
import { metersPerSecondToKmPerHour, meterToFt } from 'utils/conversionHelpers';
import {
	getOperatingPictureColumns,
	getOperatingPictureSourceOptions,
	getOperatingPictureStatusOptions
} from 'components/airSpace/airspaceConstants';
import arrow from 'assets/images/material_nav_arrow.png';
import default_airplane from 'assets/images/default_airplane.png';
import chevron from 'assets/images/material_chevron.png';
import single_engine_airplane from 'assets/images/single_engine_airplane.png';
import jet_airplane from 'assets/images/jet_airplane.png';
import helicopter from 'assets/images/helicopter.png';
import large_airplane from 'assets/images/large_airplane.png';
import simulator from 'assets/images/simulator.png';
import radar from 'assets/images/radar_dish.png';
import OwnshipCell from 'components/airSpace/bottomPanel/OwnshipCell';

import formatcoords from 'formatcoords';
import circle from '@turf/circle';
import destination from '@turf/destination';
import center from '@turf/center';
import { lineString } from '@turf/helpers';
import { CURRENT_ACCOUNT_ID } from 'constants/localStorageConstants';
import { convert } from 'services/unitConversionService';
export const getAMAircraftType = type => {
	switch (type) {
		case 'adsb': {
			return { label: t('ADSB'), value: 'adsb' };
		} //Airplane icon
		case 'asterix': {
			return { label: t('Asterix'), value: 'asterix' };
		} //Circle with arrow icon
		case 'uav': {
			return { label: t('UAV'), value: 'uav' };
		} //Drone icon
		case 'aircraft': {
			return { label: t('Aircraft'), value: 'aircraft' };
		} //Drone icon
		case 'sensor': {
			return { label: t('Sensor'), value: 'sensor' };
		} //Circle with arrow icon
		case 'unknown': {
			return { label: t('Unknown'), value: 'unknown' };
		} //Circle with arrow icon
		default: {
			return { label: t('Unknown'), value: 'unknown' };
		} //Circle with arrow icon
	}
};

export const getAMAltitudeDisplayText = (row, value) => {
	if (!value || value === 'N/A') return 'N/A';

	const convertedValue = convert(value, 'ft', 'm', { addLabel: true });

	return convertedValue;
};

export const getAMGroundSpeedDisplayText = ({ row, selectedSpeedUnit }) => {
	// Handle both flattened and nested data structures
	const provider = row?.provider || row?.properties?.provider;
	const signalType = row?.signal_type || row?.properties?.signal_type;
	const objectType = row?.object_type || row?.properties?.object_type;
	const groundSpeed = row?.ground_speed || row?.properties?.ground_speed;

	if (provider === 'aero') {
		return convert(groundSpeed, 'm/s', selectedSpeedUnit || 'mph', {
			addLabel: true,
			decimals: 0,
			ignoreUserSelectedUnitsSystem: selectedSpeedUnit !== 'mph'
		});
	}
	const isADSB = signalType === 'ADS-B';
	const isAsterix = objectType === 'asterix';
	const groundSpeedKPH = metersPerSecondToKmPerHour(groundSpeed);

	if (isADSB || isAsterix) {
		return convert(groundSpeedKPH, 'km/h', selectedSpeedUnit || 'mph', {
			addLabel: true,
			decimals: 0,
			ignoreUserSelectedUnitsSystem: selectedSpeedUnit !== 'mph'
		});
	} else if (groundSpeed !== undefined && groundSpeed !== null) {
		return convert(groundSpeed, 'm/s', selectedSpeedUnit || 'ft/s', {
			addLabel: true,
			decimals: 0,
			ignoreUserSelectedUnitsSystem: selectedSpeedUnit !== 'ft/s'
		});
	} else {
		return 'N/A';
	}
};

export const getAMAltitudeDisplay = (row, value, showLabel) => {
	// Handle both flattened and nested data structures
	const altitudeChange = row?.altitude_change || row?.properties?.altitude_change;
	const icon =
		altitudeChange > 0 ? (
			<ArrowDropUpIcon fontSize='large' color='success' />
		) : (
			<ArrowDropDownIcon fontSize='large' color='error' />
		);

	const showIcon = value !== 'N/A';
	const showUnitLabel = showLabel && value !== 'N/A';

	let convertedValue = value;
	if (value !== 'N/A') {
		convertedValue = showUnitLabel
			? convert(value, 'ft', 'm', { addLabel: true })
			: convert(value, 'ft', 'm');
	}

	return (
		value && (
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				{convertedValue} {showIcon && icon}
			</Box>
		)
	);
};

export const getAMGroundSpeedDisplay = ({ row, selectedSpeedUnit }) => {
	// Use the text version for consistency
	const textValue = getAMGroundSpeedDisplayText({ row, selectedSpeedUnit });

	// Return JSX wrapper if needed, otherwise just the text
	return textValue;
};

export const getLiveTrafficTableColumns = ({ isForPreviewProp, units, selectedSpeedUnit }) => {
	const airMeshColumns = [
		{
			field: 'actions',
			type: 'actions',
			headerName: t('Pin'),
			width: 50,
			renderCell: params => <OwnshipCell params={params.row} />,
			hideFromPreview: true
		},
		{
			field: 'timestamp',
			headerName: t('Timestamp'),
			width: 225,
			renderCell: params => {
				const timestamp = params.row?.timestamp || params.row?.properties?.timestamp || 0;
				return dayjs
					.unix(timestamp / 1000) //Receiving in microseconds
					.tz(browserTimeZone)
					.format(dateDisplayTimeMinutesSeconds);
			}
		},
		{
			field: 'provider',
			headerName: t('Provider'),
			width: 200,
			filterable: true,
			renderCell: parameters => {
				const provider = parameters.row?.provider || parameters.row?.properties?.provider || '';
				return startCase(provider);
			}
		},
		{
			field: 'provider_id',
			headerName: t('Provider ID'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.provider_id || parameters.row?.properties?.provider_id || '';
			}
		},
		{
			field: 'object_type',
			headerName: t('Object Type'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				const objectType = parameters.row?.object_type || parameters.row?.properties?.object_type;
				return getAMAircraftType(objectType)?.label || '';
			}
		},
		{
			field: 'altitude_agl_ft',
			headerName: units === 'imperial' ? t('Altitude AGL (ft)') : t('Altitude AGL (m)'),
			width: 175,
			renderCell: parameters => {
				const value =
					parameters.row?.altitude_agl_ft || parameters.row?.properties?.altitude_agl_ft;
				return getAMAltitudeDisplay(parameters.row, value, false);
			}
		},
		{
			field: 'altitude_rel_ft',
			headerName: units === 'imperial' ? t('Altitude REL (ft)') : t('Altitude REL (m)'),
			width: 175,
			renderCell: parameters => {
				const value =
					parameters.row?.altitude_rel_ft || parameters.row?.properties?.altitude_rel_ft;
				return getAMAltitudeDisplay(parameters.row, value, false);
			}
		},
		{
			field: 'altitude_msl_ft',
			headerName: units === 'imperial' ? t('Altitude MSL (ft)') : t('Altitude MSL (m)'),
			width: 175,
			renderCell: parameters => {
				const value =
					parameters.row?.altitude_msl_ft || parameters.row?.properties?.altitude_msl_ft;
				return getAMAltitudeDisplay(parameters.row, value, false);
			}
		},
		{
			field: 'ground_speed',
			headerName: t('Ground Speed'),
			width: 175,
			renderCell: parameters => {
				return getAMGroundSpeedDisplay({ row: parameters.row, selectedSpeedUnit });
			}
		},
		{
			field: 'heading',
			headerName: t('Heading'),
			width: 150,
			renderCell: params => {
				const heading = params.row?.heading || params.row?.properties?.heading;
				return convertDegreesToCardinalDirection(heading);
			}
		},
		{
			field: 'lat',
			headerName: t('Latitude'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.lat || parameters.row?.properties?.lat || '';
			}
		},
		{
			field: 'long',
			headerName: t('Longitude'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.long || parameters.row?.properties?.long || '';
			}
		},
		{
			field: 'signal_type',
			headerName: t('Signal Type'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.signal_type || parameters.row?.properties?.signal_type || t('N/A');
			}
		},
		{
			field: 'signal_provider',
			headerName: t('Signal Provider'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				const signalProvider =
					parameters.row?.signal_provider || parameters.row?.properties?.signal_provider;
				return signalProvider ? signalProvider : t('N/A');
			}
		},
		{
			field: 'id',
			width: 350,
			headerName: t('Track ID')
		},
		{
			field: 'rid',
			headerName: t('Operator ID'),
			width: 200,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.rid || parameters.row?.properties?.rid || '';
			}
		},
		{
			field: 'sn',
			headerName: t('Serial Number'),
			width: 200,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.sn || parameters.row?.properties?.sn || '';
			}
		},
		{
			field: 'operator_lat',
			headerName: t('Takeoff Latitude'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.operator_lat || parameters.row?.properties?.operator_lat || '';
			}
		},
		{
			field: 'operator_long',
			headerName: t('Takeoff Longitude'),
			width: 150,
			filterable: true,
			renderCell: parameters => {
				return parameters.row?.operator_long || parameters.row?.properties?.operator_long || '';
			}
		},
		{
			field: 'emergency_state',
			headerName: t('Emergency State'),
			width: 200,
			renderCell: parameters => {
				const emergencyState =
					parameters.row?.emergency_state || parameters.row?.properties?.emergency_state;
				return emergencyState ? t('None') : t('N/A');
			}
		},
		{
			field: 'is_sim',
			headerName: t('Simulated'),
			width: 100,
			renderCell: parameters => {
				const isSim = parameters.row?.is_sim || parameters.row?.properties?.is_sim;
				return isSim ? t('Yes') : t('No');
			}
		}
	];
	return isForPreviewProp ? airMeshColumns.filter(col => !col.hideFromPreview) : airMeshColumns;
};

export const filterTrafficData = (
	data,
	aircraftTypeFilterValue,
	providerFilterValue,
	sourceTypeFilterValue,
	signalProviderFilterValue
) => {
	return data.filter(item => {
		const matchesAircraftType =
			aircraftTypeFilterValue.length === 0 ||
			aircraftTypeFilterValue.includes(item.properties?.object_type);
		const matchesProvider =
			providerFilterValue.length === 0 || providerFilterValue.includes(item.properties?.provider);
		const matchesSourceType =
			sourceTypeFilterValue.length === 0 ||
			sourceTypeFilterValue.includes(item.properties?.signal_type);
		const matchesSignalProvider =
			!signalProviderFilterValue ||
			signalProviderFilterValue.length === 0 ||
			signalProviderFilterValue.includes(item.properties?.signal_provider);
		return matchesAircraftType && matchesProvider && matchesSourceType && matchesSignalProvider;
	});
};

export const filterOperatingPicture = (data, typeFilterValue, sourcesFilterValue) => {
	return data.filter(item => {
		const matchesType =
			typeFilterValue.length === 0 || typeFilterValue.includes(item.properties.type);
		const matchesSource =
			sourcesFilterValue.length === 0 ||
			sourcesFilterValue.includes(item.properties.is_own_account);
		return matchesType && matchesSource;
	});
};

export const formatTrafficDataForTable = (
	data,
	mapPinLocation,
	units,
	ownshipFeatures = [],
	selectedSpeedUnit,
	applyDefaultSort = true,
	maxAltitudeMetric,
	maxAltitudeImperial
) => {
	// Flatten the data structure - spread properties to top level, remove properties object
	let orderedData = data.map(item => {
		const { properties, ...rest } = item;
		return {
			...rest,
			...properties
		};
	});

	// Only apply default sorting if user hasn't chosen their own sort
	if (applyDefaultSort) {
		// If there is an ownship feature, order by distance from the ownship
		if (ownshipFeatures && ownshipFeatures.length > 0) {
			const ownshipLocation = ownshipFeatures[0].geometry.coordinates;
			// Calculate distance and sort
			orderedData.forEach(item => {
				item.distanceFromOwnship = distance(item?.geometry?.coordinates, ownshipLocation);
			});
			orderedData.sort((a, b) => a.distanceFromOwnship - b.distanceFromOwnship);
		} else {
			// If no ownship, order by altitude (lowest first) for stable sorting
			orderedData.sort((a, b) => {
				const altitudeData_a = getAltitudeInOrderOfPreference({ properties: a });
				const altitudeData_b = getAltitudeInOrderOfPreference({ properties: b });
				// Convert 'N/A' to a high number so it appears at the bottom
				const valA =
					altitudeData_a.value === 'N/A' ? Number.MAX_SAFE_INTEGER : altitudeData_a.value;
				const valB =
					altitudeData_b.value === 'N/A' ? Number.MAX_SAFE_INTEGER : altitudeData_b.value;
				return valA - valB;
			});
		}
	}

	const maxAltitudeFt =
		maxAltitudeMetric == null && maxAltitudeImperial == null
			? null
			: units === 'metric'
				? maxAltitudeMetric != null
					? meterToFt(maxAltitudeMetric)
					: null
				: maxAltitudeImperial != null
					? maxAltitudeImperial
					: null;

	const filteredData =
		maxAltitudeFt != null
			? orderedData.filter(item => {
					const altitudeData = getAltitudeInOrderOfPreference(item);
					if (altitudeData.value === 'N/A') {
						return true;
					}
					return altitudeData.value <= maxAltitudeFt;
				})
			: orderedData;

	const columns = getLiveTrafficTableColumns({ units, selectedSpeedUnit });

	return {
		columns,
		rows: filteredData
	};
};

export const formatOperatingPictureDataForTable = (data, mapPinLocation) => {
	const dataWithDistanceFromPin = data.map(index => {
		//need to validate that index.geometry is a valid geojson feature
		if (!index?.geometry || !index?.geometry?.coordinates) {
			return index;
		}

		//Find center of polygon
		const centerFeature = center(index?.geometry);

		if (!centerFeature?.geometry?.coordinates) {
			return {
				...index,
				distanceFromPin: distance(index.geometry.coordinates, mapPinLocation.geometry.coordinates)
			};
		} else {
			return index;
		}
	});

	const orderedData = orderBy(dataWithDistanceFromPin, ['distanceFromPin'], ['asc']);

	const columns = getOperatingPictureColumns();

	return {
		columns,
		rows: orderedData
	};
};

export const createGeoJsonSource = features => {
	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: features || []
		}
	};
};

export const createLineAlongHeading = (startCoordinates, heading, distanceInMiles) => {
	const options = { units: 'miles' };

	// Calculate the endpoint 2 miles away using the given heading
	const endPoint = destination(startCoordinates, distanceInMiles, heading, options);

	// Create a LineString feature from start to endpoint
	const line = lineString([startCoordinates, endPoint.geometry.coordinates]);

	return line;
};

export const createOwnshipPointFeatureSource = features => {
	//Take the feature's coordinates and add 1 mile along the heading
	const oneMilePointFeatures = features.map(feature => {
		const { geometry, properties } = feature;
		const { coordinates } = geometry;
		const heading = properties.heading;
		const point = destination(coordinates, 1, heading, { units: 'miles' });
		return {
			...point,
			properties: {
				...properties,
				heading: convertDegreesToThreeDigit(heading)
			}
		};
	});

	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: oneMilePointFeatures
		}
	};
};

export const createOwnshipFeaturesSource = features => {
	const twoMilesRadiusFeatures = features.map(feature => {
		const { geometry } = feature;
		const { coordinates } = geometry;
		const circleFeature = circle(coordinates, 2, { units: 'miles' });
		return {
			...circleFeature,
			properties: feature.properties
		};
	});

	const twoMileHeadingLineFeatures = features.map(feature => {
		const { geometry, properties } = feature;
		const { coordinates } = geometry;
		const { heading } = properties;
		const line = createLineAlongHeading(coordinates, heading, 2);
		return {
			...line,
			properties: properties
		};
	});

	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: [...twoMileHeadingLineFeatures, ...twoMilesRadiusFeatures, ...features] //include features so that there is a center point to display text
		}
	};
};

export const getAltitudeInOrderOfPreference = data => {
	const isValidAltitude = value => value !== null && value !== undefined && value !== 'N/A';

	//Display altitudes in order of preference depending on what is available in the data object:
	//1.AGL, 2. REL, 3.MSL,
	//If all are null, show AGL as 'N/A'

	if (isValidAltitude(data?.altitude_agl_ft)) {
		return {
			value: data.altitude_agl_ft,
			typeLabel: 'AGL',
			type: 'altitude_agl_ft'
		};
	} else if (isValidAltitude(data?.altitude_rel_ft)) {
		return {
			value: data.altitude_rel_ft,
			typeLabel: 'REL',
			type: 'altitude_rel_ft'
		};
	} else if (isValidAltitude(data?.altitude_msl_ft)) {
		return {
			value: data.altitude_msl_ft,
			typeLabel: 'MSL',
			type: 'altitude_msl_ft'
		};
	}
	return {
		value: 'N/A',
		typeLabel: 'AGL',
		type: 'altitude_agl_ft'
	};
};

export const setAltitudeInOrderOfPreference = data => {
	//1.AGL, 2. REL, 3.MSL
	if (data?.altitude_agl_mt) {
		return data.altitude_agl_mt;
	} else if (data?.altitude_rel_mt) {
		return data.altitude_rel_mt;
	} else if (data?.altitude_msl_mt) {
		return data.altitude_msl_mt;
	}
	return null;
};

export const formatAirmeshName = name => {
	// Example: removes '_12' from 'airmesh_sdk_12'. If there's no match, the name is unchanged.
	const removedNumberSuffix =
		name === null || name === undefined ? '' : String(name).replace(/_\d+/g, '');
	// Example:  'airmesh-sdk' to 'airmesh sdk'. If there's no match, the name is unchanged.
	const removeHyphensUnderscores =
		removedNumberSuffix === null || removedNumberSuffix === undefined
			? ''
			: String(removedNumberSuffix).replace(/[-_]/g, ' ');

	switch (removeHyphensUnderscores) {
		case 'airdex':
			return 'AirDEX';
		case 'uavionix':
			return 'uAvionix';
		case 'adsb exchange':
			return 'ADS-B Exchange';
		case 'adsb':
			return 'ADS-B';
		case 'adsl':
			return 'ADS-L';
		case 'rid':
			return 'Remote ID';
		case 'nid':
			return 'Network ID';
		case 'uas sentry':
			return 'UAS Sentry';
		case 'uav':
			return 'UAV';
		case 'dronesense':
			return 'DroneSense';
		case '':
		case null:
		case undefined:
			return 'N/A';
		default:
			return startCase(removeHyphensUnderscores);
	}
};

export const formatAirmeshDataIntoFeatures = data => {
	return data.map(index => {
		const normalizeProperties = {
			timestamp: index.observed_timestamp,
			object_type: index.object_type,
			id: index.track_id,
			provider: formatAirmeshName(index.provider),
			provider_id: index.provider_id_human,
			altitude: meterToFt(setAltitudeInOrderOfPreference(index)),
			altitude_agl_ft: index.altitude_agl_mt ? meterToFt(index.altitude_agl_mt) : 'N/A',
			altitude_rel_ft: index.altitude_rel_mt ? meterToFt(index.altitude_rel_mt) : 'N/A',
			altitude_msl_ft: index.altitude_msl_mt ? meterToFt(index.altitude_msl_mt) : 'N/A',
			heading: index.heading_deg,
			ground_speed: index.ground_speed_mps,
			lat: index.geometry.coordinates[1],
			long: index.geometry.coordinates[0],
			adsb_emitter_type: index.adsb_emitter_type,
			emergency_state: index.emergency_state,
			signal_type: formatAirmeshName(index.signal?.type),
			signal_provider: formatAirmeshName(index.signal?.provider),
			rid: index.rid,
			sn: index.sn,
			operator_lat: index?.operator_geometry?.coordinates[1],
			operator_long: index?.operator_geometry?.coordinates[0],
			is_sim: index.is_sim || false,
			view_geometry: index.view_geometry,
			cot_geometry: index.cot_geometry
		};

		return {
			type: 'Feature',
			geometry: index.geometry,
			id: index.track_id,
			properties: normalizeProperties
		};
	});
};

export const convertDegreesToCardinalDirection = degrees => {
	// Normalize degrees to be within the range of 0 to 360
	let normalizedDegrees = ((degrees % 360) + 360) % 360;

	// Define cardinal and intercardinal directions with precise boundaries
	const directions = [
		{ dir: 'N', min: 0, max: 11.25 },
		{ dir: 'NNE', min: 11.25, max: 33.75 },
		{ dir: 'NE', min: 33.75, max: 56.25 },
		{ dir: 'ENE', min: 56.25, max: 78.75 },
		{ dir: 'E', min: 78.75, max: 101.25 },
		{ dir: 'ESE', min: 101.25, max: 123.75 },
		{ dir: 'SE', min: 123.75, max: 146.25 },
		{ dir: 'SSE', min: 146.25, max: 168.75 },
		{ dir: 'S', min: 168.75, max: 191.25 },
		{ dir: 'SSW', min: 191.25, max: 213.75 },
		{ dir: 'SW', min: 213.75, max: 236.25 },
		{ dir: 'WSW', min: 236.25, max: 258.75 },
		{ dir: 'W', min: 258.75, max: 281.25 },
		{ dir: 'WNW', min: 281.25, max: 303.75 },
		{ dir: 'NW', min: 303.75, max: 326.25 },
		{ dir: 'NNW', min: 326.25, max: 348.75 },
		{ dir: 'N', min: 348.75, max: 360 }
	];

	// Find the direction that matches the normalized degrees
	const direction = directions.find(d => normalizedDegrees >= d.min && normalizedDegrees < d.max);
	const directionDisplay = direction ? direction.dir : 'N'; // Default to 'N' if no direction is found

	return `${directionDisplay} (${degrees?.toFixed(0)}°)`;
};

export const convertDegreesToThreeDigit = degrees => {
	// Normalize the heading to be within 0-360 degrees
	let normalizedHeading = ((degrees % 360) + 360) % 360;

	// Format the heading as a 3-digit string
	return normalizedHeading.toFixed(0).padStart(3, '0');
};

export const getAltitudeColor = (
	altitude,
	units,
	{ thresholdsMetric, thresholdsImperial, lowAltitudeColor, midAltitudeColor, highAltitudeColor }
) => {
	const midThresholdFt =
		units === 'metric' ? meterToFt(thresholdsMetric?.low ?? 300) : thresholdsImperial?.low ?? 1000;
	const highThresholdFt =
		units === 'metric'
			? meterToFt(thresholdsMetric?.high ?? 1500)
			: thresholdsImperial?.high ?? 5000;

	if (altitude < midThresholdFt) {
		return lowAltitudeColor;
	} else if (altitude < highThresholdFt) {
		return midAltitudeColor;
	}

	return highAltitudeColor;
};

export const getTrafficIcon = f => {
	let imgSource;
	if (f.properties?.is_sim) {
		imgSource = simulator;
	} else {
		switch (f.properties?.signal_type) {
			case 'ADS-B':
				switch (f?.properties?.adsb_emitter_type) {
					case 'single_engine':
						imgSource = single_engine_airplane;
						break;
					case 'jet':
						imgSource = jet_airplane;
						break;
					case 'helicopter':
						imgSource = helicopter;
						break;
					case 'large':
						imgSource = large_airplane;
						break;
					default:
						imgSource = default_airplane;
				}
				break;
			default:
				switch (f?.properties?.object_type) {
					case 'uav':
						imgSource = arrow;
						break;
					case 'aircraft':
						imgSource = default_airplane;
						break;
					case 'asterix':
						imgSource = chevron;
						break;
					case 'sensor':
						imgSource = radar;
						break;
					default:
						imgSource = default_airplane;
						break;
				}
		}
	}

	return imgSource;
};

export const convertCoordinatesFromDecimalToDMS = coordinates => {
	const [longitude, latitude] = coordinates;
	if (latitude && longitude) {
		const formatted = formatcoords(longitude, latitude, true);
		return formatted.format('D:M:s X', { latLonSeparator: ' / ' });
	}
};

export const convertAirMeshAircraftTypesToOptions = aircraftTypes => {
	return orderBy(
		aircraftTypes.map(aircraftType => getAMAircraftType(aircraftType)),
		['label'],
		['asc']
	);
};

export const convertAirMeshProviderTypesToOptions = providerTypes => {
	return orderBy(
		providerTypes.map(providerType => {
			return {
				label: providerType,
				value: providerType
			};
		}),
		['label'],
		['asc']
	);
};

export const convertAirMeshSignalTypesToOptions = signalTypes => {
	return orderBy(
		signalTypes.map(signalType => {
			return {
				label: signalType ? signalType : 'N/A',
				value: signalType
			};
		}),
		['label'],
		['asc']
	);
};

export const convertAirMeshSignalProvidersToOptions = signalProviders => {
	return orderBy(
		signalProviders.map(signalProvider => {
			return {
				label: signalProvider ? signalProvider : 'N/A',
				value: signalProvider
			};
		}),
		['label'],
		['asc']
	);
};

export const createAirMeshTrailsAndProjectionsSource = originalFeatures => {
	const trailFeatures = [];
	const projectionFeatures = [];

	originalFeatures.forEach(originalFeature => {
		const { geometry, properties } = originalFeature;
		const { previous_positions, heading, signal_type, object_type, timestamp } = properties;

		// Handle trails for both ADSB and Asterix
		if ((object_type === 'asterix' || signal_type === 'ADS-B') && previous_positions?.length > 0) {
			let coordinates = [];
			// Add previous positions first
			previous_positions.forEach(position => {
				coordinates.push(position.coordinates);
			});
			// Finally add the current position
			coordinates.push(geometry.coordinates);

			const feature = {
				type: 'Feature',
				geometry: {
					type: 'LineString',
					coordinates
				},
				properties: {
					signal_type,
					object_type,
					timestamp
				}
			};

			trailFeatures.push(feature);
		}

		// Only add projections for ADSB
		if (signal_type === 'ADS-B') {
			const fourMileProjection = createLineAlongHeading(geometry.coordinates, heading, 4);
			projectionFeatures.push({
				...fourMileProjection,
				properties: {
					signal_type,
					object_type,
					timestamp
				}
			});
		}
	});

	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: [...trailFeatures, ...projectionFeatures]
		}
	};
};

export const formatOperatingPictureIntoFeatures = data => {
	const currentAccountId = localStorage.getItem(CURRENT_ACCOUNT_ID);
	const features = data.map(obj => ({
		id: obj?.id,
		type: 'Feature',
		geometry: obj?.geom?.coordinates ? obj?.geom : null,
		properties: {
			...obj?.geom?.properties,
			id: obj?.id,
			operators: obj?.operators,
			starts: obj?.starts,
			ends: obj?.ends,
			status: upperCase(obj?.status),
			is_own_account: obj?.account_id?.toString() === currentAccountId,
			type: obj?.type,
			facility: obj?.facility,
			ceiling: obj?.ceiling
		}
	}));
	return features;
};

export const getOperatingPictureStatusFromValue = value => {
	return getOperatingPictureStatusOptions().find(o => o.value === value);
};

export const getOperatingPictureSourceFromValue = value => {
	return getOperatingPictureSourceOptions().find(o => o.value === value);
};

export const updatePreviousPositions = (currentFeature, featureWithNewInformation) => {
	const isADSB = featureWithNewInformation.properties.signal_type === 'ADS-B';
	const isAsterix = featureWithNewInformation.properties.object_type === 'asterix';

	// Early return if we don't need to track positions for this feature type
	if (!isAsterix && !isADSB) {
		return currentFeature.properties?.previous_positions || [];
	}

	const existingPositions = currentFeature.properties?.previous_positions || [];
	const lastPreviousPosition = existingPositions[existingPositions.length - 1];

	const previousLong = lastPreviousPosition ? lastPreviousPosition.coordinates[0] : null;
	const previousLat = lastPreviousPosition ? lastPreviousPosition.coordinates[1] : null;
	const currentLong = featureWithNewInformation.geometry.coordinates[0];
	const currentLat = featureWithNewInformation.geometry.coordinates[1];
	const isDifferentPosition = previousLong !== currentLong || previousLat !== currentLat;

	// Check if we need to add a new position
	const needsNewPosition = isDifferentPosition || existingPositions.length === 0;

	// Check if we need to filter (ADSB only - asterix keeps full trail)
	// Asterix updates infrequently, so we want to keep the full trail history
	// ADSB updates frequently, so we limit to 20 seconds to prevent overly long trails
	let needsFilter = false;
	const now = dayjs();
	const twentySecondsAgo = isADSB ? now.subtract(20, 'second') : null;

	if (isADSB && existingPositions.length > 0) {
		// Only filter if there are positions older than 20 seconds
		needsFilter = existingPositions.some(position => {
			return dayjs(position.timestamp) <= twentySecondsAgo;
		});
	}

	// If no changes needed, return existing array reference
	if (!needsNewPosition && !needsFilter) {
		return existingPositions;
	}

	let previousPositions = [...existingPositions];

	if (needsNewPosition) {
		const simplifiedFeatureForTrail = {
			coordinates: featureWithNewInformation.geometry.coordinates,
			timestamp: featureWithNewInformation.properties.timestamp
		};
		previousPositions.push(simplifiedFeatureForTrail);
	}

	if (needsFilter) {
		// This shortens ADSB trails as the aircraft moves (keeps only last 20 seconds)
		previousPositions = previousPositions.filter(previousPosition => {
			const previousPositionDate = dayjs(previousPosition.timestamp);
			return previousPositionDate > twentySecondsAgo;
		});
	}

	return previousPositions;
};

/**
 * Check if an aircraft can be pinned as an ownship
 * @param {string} aircraftType - The aircraft type to check
 * @returns {boolean} - True if the aircraft can be pinned as an ownship
 */
export const canBeOwnship = aircraftType => {
	return (
		aircraftType === 'uav' ||
		aircraftType === 'aircraft' ||
		aircraftType === 'adsb' ||
		aircraftType === 'asterix'
	);
};
